import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SimpleSelect } from '@/components/ui/select-with-more'
import { StateSelect } from '@/components/ui/StateSelect'
import { Textarea } from '@/components/ui/textarea'
import type { PolicyChangeInput, PolicyChangeKind } from '@/models/policyChange'
import { DETAIL_QUESTIONS, type DetailQuestion } from './policyChangeCopy'

/** A change with nothing on the roster to review, in the customer's words. */
export function DescribedChangeForm({
  kind,
  coverages,
  initial,
  submitLabel = 'Add change',
  onAdd,
}: {
  kind: PolicyChangeKind
  /** The coverages on the customer's policies in force. */
  coverages: string[]
  /** A saved change's answers, to edit them. */
  initial?: { details: Record<string, string>; description: string }
  submitLabel?: string
  onAdd: (change: PolicyChangeInput) => Promise<boolean>
}) {
  const questions = DETAIL_QUESTIONS[kind] ?? []
  const [answers, setAnswers] = useState<Partial<Record<string, string>>>(initial?.details ?? {})
  const [description, setDescription] = useState(initial?.description ?? '')
  const [adding, setAdding] = useState(false)
  const answered = questions.some((question) => answers[question.key]?.trim()) || description.trim()

  function answerInput(question: DetailQuestion) {
    const id = `${kind}-${question.key}`
    const value = answers[question.key] ?? ''
    const answer = (next: string) => setAnswers({ ...answers, [question.key]: next })
    const choices =
      question.fromPolicies && coverages.length > 0
        ? coverages.map((coverage) => ({ value: coverage, label: coverage }))
        : question.choices
    if (choices) {
      return (
        <SimpleSelect
          id={id}
          options={choices}
          value={value || undefined}
          onValueChange={answer}
          placeholder={question.fromPolicies ? 'Pick a coverage' : 'Pick one'}
          className="w-full"
        />
      )
    }
    if (question.state) return <StateSelect id={id} error={false} value={value} onChange={answer} />
    return <Input id={id} value={value} onChange={(event) => answer(event.target.value)} />
  }

  async function add() {
    const details = Object.fromEntries(
      Object.entries(answers).filter((entry): entry is [string, string] => Boolean(entry[1]))
    )
    setAdding(true)
    const added = await onAdd({ kind, details, description }).finally(() => setAdding(false))
    if (!added) return
    setAnswers({})
    setDescription('')
  }

  return (
    <div className="flex flex-col gap-4">
      {questions.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {questions.map((question) => (
            <Field key={question.key}>
              <FieldLabel htmlFor={`${kind}-${question.key}`} optional={question.optional}>
                {question.label}
              </FieldLabel>
              {answerInput(question)}
            </Field>
          ))}
        </div>
      )}
      <Field>
        <FieldLabel htmlFor={`${kind}-description`} optional={questions.length > 0}>
          {questions.length ? 'Anything else we should know' : 'Tell us what you need'}
        </FieldLabel>
        <Textarea
          id={`${kind}-description`}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <div>
        <Button
          type="button"
          variant="outline"
          disabled={!answered}
          onClick={add}
          loading={adding ? (initial ? 'Saving…' : 'Adding…') : undefined}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}
